import { Route, Switch, useLocation } from 'wouter';
import { useAuth } from './stores/auth';
import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Pages
import Login from './pages/Login';
import Home from './pages/Home';
import SupervisorHome from './pages/supervisor/SupervisorHome';
import TeamMemberDetail from './pages/supervisor/TeamMemberDetail';
import TeamList from './pages/supervisor/TeamList';
import TeamMap from './pages/supervisor/TeamMap';
import SupervisorAlerts from './pages/supervisor/SupervisorAlerts';
import SupervisorNav from './components/supervisor/SupervisorNav';
import RoutePage from './pages/Route';
import Visit from './pages/Visit';
import Checklist from './pages/Checklist';
import Camera from './pages/Camera';
import Profile from './pages/Profile';
import Sync from './pages/Sync';
import JustifyNoVisit from './pages/JustifyNoVisit';
import VisitSummary from './pages/VisitSummary';
import PriceCollection from './pages/PriceCollection';
import ExtraPoint from './pages/ExtraPoint';
import RuptureReport from './pages/RuptureReport';
import StockCheck from './pages/StockCheck';
import CompetitorCollection from './pages/CompetitorCollection';
import Messages from './pages/Messages';
import ForgotPassword from './pages/ForgotPassword';
import Survey from './pages/Survey';
import SurveyList from './pages/SurveyList';
import StorePicker from './pages/StorePicker';
import PesquisaPdv from './pages/PesquisaPdv';
import VisitScore from './pages/VisitScore';
import MaterialCheck from './pages/MaterialCheck';
import PromotionCheck from './pages/PromotionCheck';
import PlanogramCheck from './pages/PlanogramCheck';
import FirstAccess from './pages/FirstAccess';
import Achievements from './pages/Achievements';
import ConversationList from './pages/ConversationList';
import ChatRoom from './pages/ChatRoom';
import Training from './pages/Training';
import TrainingModule from './pages/TrainingModule';
import ExpenseForm from './pages/ExpenseForm';
import FuelForm from './pages/FuelForm';

import TrackingMap from './pages/supervisor/TrackingMap';

import { useTeamOverview } from './hooks/use-team-overview';
import { subscribeToPush, isPushSupported } from './lib/push';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { GlobalLocationTracker } from './components/GlobalLocationTracker';

// Request GPS permission early (transparent to user — no blocking UI)
function useEarlyGpsPermission() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!navigator.geolocation) return;

    // Only request once per install
    const asked = localStorage.getItem('gps-permission-asked');
    if (asked) return;

    navigator.geolocation.getCurrentPosition(
      () => { localStorage.setItem('gps-permission-asked', 'true'); },
      () => { localStorage.setItem('gps-permission-asked', 'true'); },
      { enableHighAccuracy: false, timeout: 5000 }
    );
  }, [isAuthenticated]);
}

function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  if (!needRefresh || dismissed) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-50 flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-blue-800 to-blue-500 px-5 py-3 text-white shadow-lg animate-slide-up"
    >
      <span className="text-sm font-medium">Nova versão disponível</span>
      <div className="flex gap-2">
        <button
          onClick={() => setDismissed(true)}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-blue-100 hover:text-white transition-colors"
        >
          Depois
        </button>
        <button
          onClick={() => updateServiceWorker(true)}
          className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-blue-800 shadow active:scale-95 transition-transform"
        >
          Atualizar
        </button>
      </div>
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, isLoading, setLocation]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

function SupervisorRoute({ children }: { children: React.ReactNode }) {
  const { data } = useTeamOverview();
  const alertCount = data?.alerts?.filter(a => a.status === 'OPEN').length || 0;

  return (
    <PrivateRoute>
      <>
        {children}
        <SupervisorNav alertCount={alertCount} />
      </>
    </PrivateRoute>
  );
}

function SupervisorRedirect() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (user?.role === 'SUPERVISOR') {
      setLocation('/supervisor');
    }
  }, [user, setLocation]);

  return null;
}

export default function App() {
  const { loadUser } = useAuth();
  useEarlyGpsPermission();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    isPushSupported().then((supported) => {
      if (supported && Notification.permission === 'default') {
        // Ask after first successful login
        const hasAsked = localStorage.getItem('push-asked');
        if (!hasAsked) {
          subscribeToPush().then(() => {
            localStorage.setItem('push-asked', 'true');
          });
        }
      }
    });
  }, []);

  return (
    <ErrorBoundary>
    <ToastProvider>
    <div className="min-h-screen bg-background">
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/first-access" component={FirstAccess} />

        <Route path="/">
          <PrivateRoute>
            <SupervisorRedirect />
            <Home />
          </PrivateRoute>
        </Route>

        {/* Supervisor routes */}
        <Route path="/supervisor">
          <SupervisorRoute>
            <SupervisorHome />
          </SupervisorRoute>
        </Route>

        <Route path="/supervisor/team/:id">
          {(params) => (
            <SupervisorRoute>
              <TeamMemberDetail promoterId={params.id as string} />
            </SupervisorRoute>
          )}
        </Route>

        <Route path="/supervisor/team">
          <SupervisorRoute>
            <TeamList />
          </SupervisorRoute>
        </Route>

        <Route path="/supervisor/map">
          <SupervisorRoute>
            <TeamMap />
          </SupervisorRoute>
        </Route>

        <Route path="/supervisor/alerts">
          <SupervisorRoute>
            <SupervisorAlerts />
          </SupervisorRoute>
        </Route>

        <Route path="/supervisor/tracking">
          <SupervisorRoute>
            <TrackingMap />
          </SupervisorRoute>
        </Route>

        <Route path="/route">
          <PrivateRoute>
            <RoutePage />
          </PrivateRoute>
        </Route>

        <Route path="/visit/:storeId">
          {(params) => (
            <PrivateRoute>
              <Visit storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/checklist/:visitId">
          {(params) => (
            <PrivateRoute>
              <Checklist visitId={params.visitId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/camera/:visitId">
          {(params) => (
            <PrivateRoute>
              <Camera visitId={params.visitId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/profile">
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        </Route>

        <Route path="/sync">
          <PrivateRoute>
            <Sync />
          </PrivateRoute>
        </Route>

        <Route path="/achievements">
          <PrivateRoute>
            <Achievements />
          </PrivateRoute>
        </Route>

        <Route path="/training">
          <PrivateRoute>
            <Training />
          </PrivateRoute>
        </Route>

        <Route path="/training/:pathId">
          {(params) => (
            <PrivateRoute>
              <TrainingModule pathId={params.pathId!} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/expense">
          <PrivateRoute>
            <ExpenseForm />
          </PrivateRoute>
        </Route>

        <Route path="/fuel">
          <PrivateRoute>
            <FuelForm />
          </PrivateRoute>
        </Route>

        <Route path="/justify/:storeId">
          {(params) => (
            <PrivateRoute>
              <JustifyNoVisit storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/justify/:storeId/:routeId">
          {(params) => (
            <PrivateRoute>
              <JustifyNoVisit storeId={params.storeId} routeId={params.routeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/visit-summary/:visitId">
          {(params) => (
            <PrivateRoute>
              <VisitSummary visitId={params.visitId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/prices/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <PriceCollection visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/extra-point/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <ExtraPoint visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/rupture/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <RuptureReport visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/stock-check/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <StockCheck visitId={params.visitId!} storeId={params.storeId!} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/competitor-collection/:storeId/:visitId">
          {(params) => (
            <PrivateRoute>
              <CompetitorCollection storeId={params.storeId!} visitId={params.visitId!} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/messages">
          <PrivateRoute>
            <Messages />
          </PrivateRoute>
        </Route>

        <Route path="/conversations">
          <PrivateRoute>
            <ConversationList />
          </PrivateRoute>
        </Route>

        <Route path="/chat/:conversationId">
          {(params) => (
            <PrivateRoute>
              <ChatRoom conversationId={params.conversationId!} />
            </PrivateRoute>
          )}
        </Route>

        {/* Pesquisa PDV avulsa (sem visita) — atalho da Home para agências */}
        <Route path="/pesquisa-pdv">
          {() => (
            <PrivateRoute>
              <StorePicker />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/pesquisa-pdv/:storeId">
          {(params) => (
            <PrivateRoute>
              <SurveyList storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/survey/:surveyId/loja/:storeId">
          {(params) => (
            <PrivateRoute>
              <Survey surveyId={params.surveyId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        {/* Pesquisa PDV oficial (sortimento + preço, formato do formulário
            impresso) — a lista genérica de pesquisas (SurveyList) redireciona
            para cá quando a survey ativa é a oficial (título contém
            "Pesquisa Oficial"). :visitId opcional cobre tanto o fluxo dentro
            da visita quanto o avulso (agências sem roteiro). */}
        <Route path="/pesquisa-oficial/:storeId/:visitId?">
          {(params) => (
            <PrivateRoute>
              <PesquisaPdv storeId={params.storeId!} visitId={params.visitId} />
            </PrivateRoute>
          )}
        </Route>

        {/* Pesquisa dentro da visita (fluxo merchandising) */}
        <Route path="/surveys/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <SurveyList visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/survey/:surveyId/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <Survey surveyId={params.surveyId} visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/survey/:surveyId">
          {(params) => (
            <PrivateRoute>
              <Survey surveyId={params.surveyId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/visit-score/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <VisitScore visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/materials/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <MaterialCheck visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/promotions/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <PromotionCheck visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route path="/planogram/:visitId/:storeId">
          {(params) => (
            <PrivateRoute>
              <PlanogramCheck visitId={params.visitId} storeId={params.storeId} />
            </PrivateRoute>
          )}
        </Route>

        <Route>
          <div className="flex min-h-screen items-center justify-center">
            <p>Página não encontrada</p>
          </div>
        </Route>
      </Switch>
      <UpdateBanner />
      <GlobalLocationTracker />
    </div>
    </ToastProvider>
    </ErrorBoundary>
  );
}
